import React, { useState, useEffect, useCallback } from 'react';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import Properties from './components/Properties';
import FileDropzone from './components/FileDropzone';
import BrokerManagement from './components/BrokerManagement';
import BrokerPortal from './components/BrokerPortal';
import './App.css';

export interface Property {
  id: string;
  tenant?: string;
  title?: string;
  phone?: string;
  fullAddress?: string;
  address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  entityOwnership?: string;
  mailingAddress?: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
  geocodeSource?: string;
  googlePlaceId?: string | null;
  googleFormattedAddress?: string;
  googlePartialMatch?: boolean;
  reportAllParcel?: Record<string, any> | null;
  reportAllOwner?: string;
  starred?: boolean;
  notes?: string;
  ownerContacts?: OwnerContact[];
  brokerAdditions?: BrokerAddition[];
  createdAt: string;
}

export interface BrokerAddition {
  id: string;
  brokerId: string;
  brokerName: string;
  note?: string;
  ownerContact?: OwnerContact | null;
  createdAt: string;
}

export interface OwnerContact {
  firstName?: string;
  lastName?: string;
  age?: string;
  cell?: string;
  additionalPhones?: string[];
  email?: string;
  address?: string;
}

export interface Broker {
  id: string;
  name: string;
  email: string;
  company: string;
  username: string;
  hasCredentials: boolean;
  isActive: boolean;
  propertyIds: string[];
  createdAt: string;
  updatedAt: string;
}

export type AppMode = 'admin' | 'broker';
export type AppView = 'properties' | 'upload' | 'brokers' | 'brokerPortal';

function authHeaders(token: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

function App() {
  const [adminToken, setAdminToken] = useState<string | null>(() =>
    localStorage.getItem('crm_admin_token'),
  );
  const [isVerifying, setIsVerifying] = useState(!!localStorage.getItem('crm_admin_token'));
  const [mode, setMode] = useState<AppMode>(() => {
    const saved = localStorage.getItem('crm_mode');
    return saved === 'broker' ? 'broker' : 'admin';
  });
  const [activeView, setActiveView] = useState<AppView>(() => {
    const saved = localStorage.getItem('crm_view');
    if (saved === 'properties' || saved === 'upload' || saved === 'brokers' || saved === 'brokerPortal') {
      return saved;
    }
    return 'properties';
  });
  const [properties, setProperties] = useState<Property[]>([]);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Verify existing token on mount
  useEffect(() => {
    const token = localStorage.getItem('crm_admin_token');
    if (!token) {
      setIsVerifying(false);
      return;
    }
    fetch('/api/admin/verify', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.ok) {
          setAdminToken(token);
          setMode('admin');
        } else {
          localStorage.removeItem('crm_admin_token');
          setAdminToken(null);
        }
      })
      .catch(() => {
        localStorage.removeItem('crm_admin_token');
        setAdminToken(null);
      })
      .finally(() => setIsVerifying(false));
  }, []);

  const handleAdminLogin = useCallback((token: string) => {
    localStorage.setItem('crm_admin_token', token);
    setAdminToken(token);
    setMode('admin');
    setActiveView('properties');
  }, []);

  const [initialBrokerData, setInitialBrokerData] = useState<{ broker: Broker; properties: Property[] } | null>(null);

  const handleBrokerLoginFromLoginPage = useCallback((broker: Broker, brokerProperties: Property[]) => {
    setInitialBrokerData({ broker, properties: brokerProperties });
    setMode('broker');
    setActiveView('brokerPortal');
  }, []);

  const handleLogout = useCallback(async () => {
    if (adminToken) {
      fetch('/api/admin/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
      }).catch(() => {});
    }
    localStorage.removeItem('crm_admin_token');
    localStorage.removeItem('crm_mode');
    localStorage.removeItem('crm_view');
    setAdminToken(null);
    setInitialBrokerData(null);
    setMode('admin');
    setActiveView('properties');
  }, [adminToken]);

  useEffect(() => {
    localStorage.setItem('crm_mode', mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem('crm_view', activeView);
  }, [activeView]);

  useEffect(() => {
    if (mode === 'admin' && !['properties', 'upload', 'brokers'].includes(activeView)) {
      setActiveView('properties');
    }
    if (mode === 'broker' && activeView !== 'brokerPortal') {
      setActiveView('brokerPortal');
    }
  }, [mode, activeView]);

  const fetchProperties = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/properties');
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchBrokers = useCallback(async () => {
    if (!adminToken) return;
    try {
      const response = await fetch('/api/brokers', {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setBrokers(data);
      }
    } catch (error) {
      console.error('Error fetching brokers:', error);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken && mode === 'admin') {
      fetchProperties();
      fetchBrokers();
    }
  }, [adminToken, mode, fetchProperties, fetchBrokers]);

  const handleFileUpload = async (file: File) => {
    if (!adminToken) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await fetch('/api/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to upload file');
      }
      const result = await response.json();
      await fetchProperties();
      await fetchBrokers();
      setActiveView('properties');
      if (result.message) {
        alert(result.message);
      }
    } catch (error: any) {
      alert(`Error: ${error.message}`);
      console.error('Upload error:', error);
    }
  };

  const handleDeleteProperty = async (id: string) => {
    if (!adminToken) return;
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    try {
      const response = await fetch(`/api/properties/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!response.ok) throw new Error('Failed to delete property');
      await fetchProperties();
      await fetchBrokers();
    } catch (error) {
      alert('Error deleting property');
      console.error('Delete error:', error);
    }
  };

  const handleBulkDeleteProperties = async (ids: string[]) => {
    if (!adminToken) return;
    const uniqueIds = Array.from(new Set((ids || []).filter((id) => typeof id === 'string' && id.trim().length > 0)));
    if (uniqueIds.length === 0) return;
    try {
      const response = await fetch('/api/properties/bulk-delete', {
        method: 'POST',
        headers: authHeaders(adminToken),
        body: JSON.stringify({ ids: uniqueIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to delete properties');
      }
      await fetchProperties();
      await fetchBrokers();
    } catch (error: any) {
      alert(`Error deleting selected properties: ${error.message || String(error)}`);
      console.error('Bulk delete error:', error);
    }
  };

  const handleUpdateProperty = async (updated: Property) => {
    setProperties((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  };

  const handleCreateBroker = async (payload: { name: string; email: string; company: string; username?: string; password?: string }) => {
    if (!adminToken) return;
    try {
      const response = await fetch('/api/brokers', {
        method: 'POST',
        headers: authHeaders(adminToken),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create broker');
      }
      await fetchBrokers();
    } catch (error: any) {
      alert(`Error creating broker: ${error.message || String(error)}`);
    }
  };

  const handleUpdateBroker = async (id: string, payload: Partial<Broker>) => {
    if (!adminToken) return;
    try {
      const response = await fetch(`/api/brokers/${id}`, {
        method: 'PUT',
        headers: authHeaders(adminToken),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to update broker');
      }
      await fetchBrokers();
    } catch (error: any) {
      alert(`Error updating broker: ${error.message || String(error)}`);
    }
  };

  const handleUpdateBrokerPropertyAccess = async (id: string, propertyIds: string[]) => {
    if (!adminToken) return;
    try {
      const response = await fetch(`/api/brokers/${id}/property-access`, {
        method: 'PUT',
        headers: authHeaders(adminToken),
        body: JSON.stringify({ propertyIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to update property access');
      }
      await fetchBrokers();
    } catch (error: any) {
      alert(`Error updating property access: ${error.message || String(error)}`);
    }
  };

  const handleSetBrokerCredentials = async (id: string, username: string, password: string) => {
    if (!adminToken) return;
    try {
      const response = await fetch(`/api/brokers/${id}/credentials`, {
        method: 'PUT',
        headers: authHeaders(adminToken),
        body: JSON.stringify({ username, password }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to set credentials');
      }
      await fetchBrokers();
    } catch (error: any) {
      alert(`Error setting credentials: ${error.message || String(error)}`);
    }
  };

  const handleDeleteBroker = async (id: string) => {
    if (!adminToken) return;
    if (!window.confirm('Are you sure you want to delete this broker?')) return;
    try {
      const response = await fetch(`/api/brokers/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!response.ok) throw new Error('Failed to delete broker');
      await fetchBrokers();
    } catch (error: any) {
      alert(`Error deleting broker: ${error.message || String(error)}`);
    }
  };

  if (isVerifying) {
    return (
      <div className="app" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div className="loading-state">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  // Not logged in as admin — show login page (unless broker mode)
  if (!adminToken && mode !== 'broker') {
    return <Login onAdminLogin={handleAdminLogin} onBrokerLogin={handleBrokerLoginFromLoginPage} />;
  }

  // Broker mode (no admin token needed — broker has own login inside BrokerPortal)
  if (mode === 'broker') {
    return (
      <div className="app">
        <Sidebar
          activeView={activeView}
          onViewChange={(view) => setActiveView(view as AppView)}
          propertyCount={properties.length}
          brokerCount={brokers.length}
          mode={mode}
          onModeChange={(m) => {
            if (m === 'admin') {
              setMode('admin');
              if (!adminToken) {
                setActiveView('properties');
              }
            } else {
              setMode(m);
            }
          }}
          onLogout={handleLogout}
        />
        <div className="app-main">
          <BrokerPortal
            initialBroker={initialBrokerData?.broker}
            initialProperties={initialBrokerData?.properties}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <Sidebar
        activeView={activeView}
        onViewChange={(view) => setActiveView(view as AppView)}
        propertyCount={properties.length}
        brokerCount={brokers.length}
        mode={mode}
        onModeChange={setMode}
        onLogout={handleLogout}
      />
      <div className="app-main">
        {activeView === 'properties' ? (
          isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading properties...</p>
            </div>
          ) : (
            <Properties
              properties={properties}
              onDeleteProperty={handleDeleteProperty}
              onDeleteProperties={handleBulkDeleteProperties}
              onUpdateProperty={handleUpdateProperty}
              enableAddressLinks={true}
            />
          )
        ) : activeView === 'upload' ? (
          <FileDropzone
            onFileUpload={handleFileUpload}
            onClose={() => setActiveView('properties')}
            isFullPage={true}
          />
        ) : activeView === 'brokers' ? (
          <BrokerManagement
            brokers={brokers}
            properties={properties}
            onCreateBroker={handleCreateBroker}
            onUpdateBroker={handleUpdateBroker}
            onUpdateBrokerPropertyAccess={handleUpdateBrokerPropertyAccess}
            onSetBrokerCredentials={handleSetBrokerCredentials}
            onDeleteBroker={handleDeleteBroker}
          />
        ) : null}
      </div>
    </div>
  );
}

export default App;
